import { Link } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';

import type {
  ImportJobPhase,
  ImportJobResponse,
} from '~entities/import-job/api/types';

import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Progress } from '~shared/shadcn/ui/progress';

function phaseLabel(phase: ImportJobPhase | undefined): string {
  switch (phase) {
    case 'classes':
      return 'Creating classes…';
    case 'images':
      return 'Uploading class photos…';
    case 'parents':
      return 'Creating families…';
    case 'emails':
      return 'Sending welcome emails…';
    default:
      return 'Working…';
  }
}

interface Step5ReviewProps {
  classCount: number;
  photoCount: number;
  familyCount: number;
  studentEstimate: number;
  welcomeEmailEnabled: boolean;
  job: ImportJobResponse | null;
  isLaunchBusy: boolean;
  launchInitiated: boolean;
  launchError: string | null;
  isClosing: boolean;
  isCancelling?: boolean;
  onLaunch: () => void;
  onCloseSession: () => void;
  /** Cancel a hung/running import and leave the session so a new one can start. */
  onCancelAndExit?: () => void;
  onBack: () => void;
  onDownloadErrors: (dataset: 'classes' | 'parents') => void;
}

export function Step5Review({
  classCount,
  photoCount,
  familyCount,
  studentEstimate,
  welcomeEmailEnabled,
  job,
  isLaunchBusy,
  launchInitiated,
  launchError,
  isClosing,
  isCancelling = false,
  onLaunch,
  onCloseSession,
  onCancelAndExit,
  onBack,
  onDownloadErrors,
}: Step5ReviewProps) {
  const isRunning =
    job?.status === 'queued' || job?.status === 'processing';
  const isDone = job?.status === 'completed';
  const isFailed = job?.status === 'failed';
  const isPreparingLaunch =
    isLaunchBusy && !isRunning && !isDone && !isFailed && !launchError;
  const isLaunchFailed = !!launchError && !isRunning && !isDone && !isFailed;

  const phaseProgress = () => {
    if (!job || !isRunning) return 0;

    let totalWeight = 0;
    let earnedWeight = 0;

    if (job.totalClasses > 0) {
      totalWeight += 25;
      earnedWeight += (job.classesProcessed / job.totalClasses) * 25;
    }
    if (job.totalImages > 0) {
      totalWeight += 25;
      earnedWeight += (job.imagesProcessed / job.totalImages) * 25;
    }
    if (job.totalParents > 0) {
      totalWeight += 35;
      earnedWeight += (job.parentsProcessed / job.totalParents) * 35;
    }
    if (job.welcomeEmailEnabled && job.totalParents > 0) {
      totalWeight += 15;
      earnedWeight += (job.emailsSent / job.totalParents) * 15;
    }

    if (totalWeight === 0) return 0;
    return Math.min(100, Math.round((earnedWeight / totalWeight) * 100));
  };

  if (isPreparingLaunch || isLaunchFailed || isRunning || isDone || isFailed) {
    return (
      <Card className='mx-auto max-w-3xl'>
        <CardContent className='space-y-6 p-6'>
          <h2 className='text-xl font-semibold'>
            {isDone
              ? 'Import complete'
              : isFailed || isLaunchFailed
                ? 'Import stopped'
                : isPreparingLaunch
                  ? 'Starting your import'
                  : 'Setting up your studio'}
          </h2>
          {isPreparingLaunch && (
            <>
              <p className='text-muted-foreground'>
                Saving your files and queuing the import. This may take a moment
                for large uploads.
              </p>
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                Preparing…
              </div>
            </>
          )}
          {isRunning && (
            <>
              <p className='text-muted-foreground'>
                You can close this page — we&apos;ll keep working in the
                background. If progress is stuck, stop the import below to start
                a new one.
              </p>
              <div className='space-y-1'>
                <div className='flex justify-between text-sm text-muted-foreground'>
                  <span>{phaseLabel(job?.phase)}</span>
                  <span className='tabular-nums'>{phaseProgress()}%</span>
                </div>
                <Progress value={phaseProgress()} />
              </div>
              <dl className='grid grid-cols-2 gap-2 text-sm'>
                <div>Classes</div>
                <div>
                  {job?.classesProcessed ?? 0} / {job?.totalClasses ?? 0}
                </div>
                <div>Photos</div>
                <div>
                  {job?.imagesProcessed ?? 0} / {job?.totalImages ?? 0}
                </div>
                <div>Families</div>
                <div>
                  {job?.parentsProcessed ?? 0} / {job?.totalParents ?? 0}
                </div>
                {job?.welcomeEmailEnabled && (
                  <>
                    <div>Emails sent</div>
                    <div>{job.emailsSent}</div>
                  </>
                )}
              </dl>
              {onCancelAndExit && (
                <div className='space-y-2 pt-2'>
                  <p className='text-xs text-muted-foreground'>
                    Stopping keeps anything already imported (shown in the counts
                    above). You can review those records in Import history, then
                    start a fresh import.
                  </p>
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      variant='outline'
                      disabled={isCancelling || isClosing}
                      onClick={onCancelAndExit}
                    >
                      {isCancelling ? 'Stopping…' : 'Stop import & start over'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
          {isPreparingLaunch && onCancelAndExit && (
            <div className='flex flex-wrap gap-2 pt-2'>
              <Button
                variant='outline'
                disabled={isCancelling || isClosing}
                onClick={onCancelAndExit}
              >
                {isCancelling ? 'Stopping…' : 'Cancel & start over'}
              </Button>
            </div>
          )}
          {isLaunchFailed && (
            <div className='space-y-4'>
              <p className='text-muted-foreground'>{launchError}</p>
              <Button variant='outline' asChild>
                <Link to='/import-data-logs'>Import history</Link>
              </Button>
              <Button
                variant='secondary'
                disabled={isClosing}
                onClick={onCloseSession}
              >
                {isClosing ? 'Closing…' : 'Close import session'}
              </Button>
            </div>
          )}
          {isDone && (
            <div className='space-y-4'>
              <p className='text-muted-foreground'>
                Your classes and families are ready. Review anything that needs
                a quick look below.
              </p>
              {(job?.errors?.length ?? 0) > 0 && (
                <div className='flex flex-wrap gap-2'>
                  {job?.errors?.some((e) => e.phase === 'classes') && (
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => onDownloadErrors('classes')}
                    >
                      Download class issues (.csv)
                    </Button>
                  )}
                  {job?.errors?.some((e) => e.phase === 'parents') && (
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => onDownloadErrors('parents')}
                    >
                      Download family issues (.csv)
                    </Button>
                  )}
                </div>
              )}
              <div className='flex flex-wrap gap-2'>
                <Button asChild>
                  <Link to='/classes'>Open classes</Link>
                </Button>
                <Button variant='outline' asChild>
                  <Link to='/students'>View families</Link>
                </Button>
                <Button variant='outline' asChild>
                  <Link to='/import-data-logs'>Import history</Link>
                </Button>
                <Button
                  variant='secondary'
                  disabled={isClosing}
                  onClick={onCloseSession}
                >
                  {isClosing ? 'Closing…' : 'Done — close import session'}
                </Button>
              </div>
            </div>
          )}
          {isFailed && (
            <div className='space-y-4'>
              <p className='text-muted-foreground'>
                Something went wrong during import. Download error details below
                if available, then close this session when you are finished
                reviewing.
              </p>
              {(job?.errors?.length ?? 0) > 0 && (
                <div className='flex flex-wrap gap-2'>
                  {job?.errors?.some((e) => e.phase === 'classes') && (
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => onDownloadErrors('classes')}
                    >
                      Download class issues (.csv)
                    </Button>
                  )}
                  {job?.errors?.some((e) => e.phase === 'parents') && (
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => onDownloadErrors('parents')}
                    >
                      Download family issues (.csv)
                    </Button>
                  )}
                </div>
              )}
              <Button variant='outline' asChild>
                <Link to='/import-data-logs'>Import history</Link>
              </Button>
              <Button
                variant='secondary'
                disabled={isClosing}
                onClick={onCloseSession}
              >
                {isClosing ? 'Closing…' : 'Close import session'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='mx-auto max-w-3xl'>
      <CardContent className='space-y-6 p-6'>
        <h2 className='text-xl font-semibold'>Ready to launch</h2>
        <div className='rounded-lg border bg-muted/30 p-4 text-sm leading-relaxed'>
          <p>You&apos;re about to:</p>
          <ul className='mt-2 list-inside list-disc space-y-1'>
            {classCount > 0 && (
              <li>
                Create {classCount} class{classCount === 1 ? '' : 'es'}
                {photoCount > 0
                  ? ` (with photos for ${photoCount})`
                  : ''}
              </li>
            )}
            {familyCount > 0 && (
              <li>
                Create {familyCount} famil
                {familyCount === 1 ? 'y' : 'ies'}
                {studentEstimate > 0
                  ? ` and about ${studentEstimate} student${studentEstimate === 1 ? '' : 's'}`
                  : ''}
              </li>
            )}
            <li>
              {welcomeEmailEnabled
                ? `Send a welcome email to ${familyCount} famil${familyCount === 1 ? 'y' : 'ies'}`
                : 'Skip welcome emails for this import'}
            </li>
          </ul>
          <p className='mt-4 text-muted-foreground'>
            You can step away after launching — we&apos;ll keep working in the
            background.
          </p>
        </div>
        <div className='flex justify-between'>
          <Button variant='outline' onClick={onBack} disabled={launchInitiated}>
            Back
          </Button>
          <Button
            disabled={launchInitiated || isLaunchBusy}
            onClick={onLaunch}
          >
            {isLaunchBusy ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' />
                Starting…
              </>
            ) : (
              'Looks good, launch'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
